import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import { Clock } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import { Switch } from '@/components/atoms/switch';
import { Label } from '@/components/atoms/label';
import { FormField } from '@/components/molecules/form-field';
import { DAYS } from '@/lib/constants/form-options';
import type { CreateShopFormData, UpdateShopFormData } from '../types/shop-form.types';

type DayType = typeof DAYS[number];

interface ShopScheduleProps<T extends CreateShopFormData | UpdateShopFormData> {
  form: UseFormReturn<T>;
}

export function ShopSchedule<T extends CreateShopFormData | UpdateShopFormData>({
  form,
}: ShopScheduleProps<T>) {
  const { t } = useTranslation();

  const renderDaySchedule = (day: DayType) => {
    const dayLabel = t(`shops.days.${day}`);
    const isEnabled = form.watch(`${day}.enable` as any);

    return (
      <div key={day} className="py-3 border-b last:border-0 space-y-2">
        {/* Day toggle row */}
        <div className="flex items-center justify-between">
          <FormField
            control={form.control}
            name={`${day}.enable` as any}
            render={({ field }) => (
              <div className="flex items-center gap-3">
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <Label className="font-medium">{dayLabel}</Label>
              </div>
            )}
          />
          {!isEnabled && (
            <span className="text-muted-foreground text-sm">{t('shops.closed')}</span>
          )}
        </div>

        {/* Time inputs - column on mobile, row on desktop */}
        {isEnabled && (
          <div className="flex flex-col gap-3 mt-2">
            {/* Morning */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t('shops.morning', { defaultValue: 'Matin' })}</span>
              <div className="flex items-center gap-2">
                <FormField
                  control={form.control}
                  name={`${day}.startDateMorning` as any}
                  render={({ field }) => (
                    <Input
                      type="time"
                      className="flex-1 h-10"
                      {...field}
                    />
                  )}
                />
                <span className="text-muted-foreground">-</span>
                <FormField
                  control={form.control}
                  name={`${day}.closeDateMorning` as any}
                  render={({ field }) => (
                    <Input
                      type="time"
                      className="flex-1 h-10"
                      {...field}
                    />
                  )}
                />
              </div>
            </div>

            {/* Afternoon */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t('shops.afternoon', { defaultValue: 'Après-midi' })}</span>
              <div className="flex items-center gap-2">
                <FormField
                  control={form.control}
                  name={`${day}.startingDateAfternoon` as any}
                  render={({ field }) => (
                    <Input
                      type="time"
                      className="flex-1 h-10"
                      {...field}
                    />
                  )}
                />
                <span className="text-muted-foreground">-</span>
                <FormField
                  control={form.control}
                  name={`${day}.closeDateAfternoon` as any}
                  render={({ field }) => (
                    <Input
                      type="time"
                      className="flex-1 h-10"
                      {...field}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="size-5" />
          {t('shops.openingHours')}
        </CardTitle>
        <CardDescription>{t('shops.openingHoursDescription')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {DAYS.map((day) => renderDaySchedule(day))}
        </div>
      </CardContent>
    </Card>
  );
}
